import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, ChevronRight, GraduationCap, Play, ShieldCheck, ChevronLeft, Activity } from 'lucide-react';
import { useLearn } from '../../contexts/LearnContext';
import { LEARN_CONTENTS } from '../../constants/learnContents';
import { LearnLesson } from './LearnLesson';

export const LearnSidebar: React.FC = () => {
    const { isLearnOpen, setIsLearnOpen, currentLessonId, setCurrentLessonId } = useLearn();

    const lessons = [
        { id: 'getting-started', title: 'Getting Started', icon: <Play size={18} />, category: 'Basics' },
        { id: 'incident-reporting', title: 'Incident Reporting', icon: <Activity size={18} />, category: 'Resolve Core' },
        { id: 'fault-tree-analysis', title: 'Fault Tree Architect', icon: <GraduationCap size={18} />, category: 'Advanced RCA' },
        { id: 'reliability-analytics', title: 'Reliability Analytics', icon: <BookOpen size={18} />, category: 'Lab' },
        { id: 'resolution-sync', title: 'FMEA Loopback', icon: <ShieldCheck size={18} />, category: 'Sync' },
    ];

    const currentLesson = currentLessonId ? LEARN_CONTENTS[currentLessonId] : null;

    return (
        <AnimatePresence>
            {isLearnOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsLearnOpen(false)}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
                    />

                    {/* Sidebar */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-[450px] bg-white dark:bg-slate-900 shadow-2xl z-[101] flex flex-col border-l border-slate-200 dark:border-slate-800"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
                            <div className="flex items-center gap-3">
                                <GraduationCap size={24} />
                                <div>
                                    <h2 className="font-bold text-lg">Magnus Academy</h2>
                                    <p className="text-xs text-emerald-100 italic">Living Reliability Education</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsLearnOpen(false)}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {!currentLessonId ? (
                                <div className="space-y-6">
                                    <section>
                                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 px-2">Knowledge Paths</h3>
                                        <div className="space-y-2">
                                            {lessons.map(lesson => (
                                                <button
                                                    key={lesson.id}
                                                    onClick={() => setCurrentLessonId(lesson.id)}
                                                    className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all border border-transparent hover:border-slate-200 dark:hover:border-slate-700 group"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg group-hover:scale-110 transition-transform">
                                                            {lesson.icon}
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="font-semibold text-slate-700 dark:text-slate-200">{lesson.title}</p>
                                                            <p className="text-xs text-slate-500">{lesson.category}</p>
                                                        </div>
                                                    </div>
                                                    <ChevronRight size={16} className="text-slate-400 group-hover:translate-x-1 transition-transform" />
                                                </button>
                                            ))}
                                        </div>
                                    </section>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <button
                                        onClick={() => setCurrentLessonId(null)}
                                        className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1 mb-6 group"
                                    >
                                        <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> Back to Academy
                                    </button>

                                    {currentLesson ? (
                                        <LearnLesson content={currentLesson} />
                                    ) : (
                                        <div className="text-center py-20">
                                            <p className="text-slate-400 italic">This lesson is under construction.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 mt-auto">
                            <p className="text-[10px] text-center text-slate-400 font-medium">
                                Magnus Learn &copy; 2026 AutoGain Insight Systems LLC
                            </p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};
